/*! @wharfkit/contract v4.0.0-rc5 | BSD-3-Clause | Copyright (c) 2022 Greymass Inc. | https://github.com/wharfkit/js */
import { ABI, Action, Blob, Checksum160, Checksum256, Float64, Name, PermissionLevel, Serializer, Transaction, UInt128, UInt32, UInt64, isInstanceOf } from "@wharfkit/antelope";
import { PlaceholderAuth } from "@wharfkit/signing-request";
import { ABICache } from "@wharfkit/abicache";
//#region src/utils.ts
function pascalCase(value) {
	return value.split(/_| /).map((w) => {
		return w[0].toUpperCase() + w.slice(1).toLowerCase();
	}).join("");
}
function capitalize(string) {
	if (typeof string !== "string" || string.length === 0) return "";
	return string.charAt(0).toUpperCase() + string.slice(1);
}
function singularize(word) {
	if (word.endsWith("ies")) return word.slice(0, -3) + "y";
	else if (word.endsWith("ches") || word.endsWith("ses")) return word.slice(0, -2);
	else if (word.endsWith("s") && word.length > 1 && word[word.length - 2] !== "s") return word.slice(0, -1);
	else return word;
}
function indexPositionInWords(index) {
	return [
		"primary",
		"secondary",
		"tertiary",
		"fourth",
		"fifth",
		"sixth",
		"seventh",
		"eighth",
		"ninth",
		"tenth"
	][index];
}
function wrapIndexValue(value) {
	if (value === void 0 || value === null) return;
	if (isInstanceOf(value, UInt128) || isInstanceOf(value, UInt64) || isInstanceOf(value, Float64) || isInstanceOf(value, Checksum256) || isInstanceOf(value, Checksum160)) return value;
	if (typeof value === "number") return UInt64.from(value);
	return Name.from(value);
}
function isAbsentScope(value) {
	return value === void 0 || value === null || value === "";
}
function wrapScopeValue(value) {
	if (value === void 0 || value === null) throw new Error("Scope is required");
	if (isInstanceOf(value, Name)) return value;
	if (typeof value === "string") return value;
	if (typeof value === "number" && !Number.isSafeInteger(value)) throw new Error(`Scope ${value} is not an integer a number can hold, use UInt64.from() to pass it instead`);
	return UInt64.from(value);
}
function abiToBlob(abi) {
	const serializedABI = Serializer.encode({
		object: abi,
		type: ABI
	});
	return new Blob(serializedABI.array);
}
function blobStringToAbi(blobString) {
	const blob = Blob.from(blobString);
	return ABI.from(blob);
}
function formatExceptionMessage(except) {
	const top = except.stack?.[0];
	if (top?.format) {
		const data = top.data ?? {};
		const substituted = top.format.replace(/\$\{(\w+)\}/g, (_, key) => key in data ? String(data[key]) : `\${${key}}`);
		if (substituted) return substituted;
	}
	if (typeof top?.data?.s === "string") return top.data.s;
	return except.message;
}
//#endregion
//#region src/contract/table-cursor.ts
let _Symbol$asyncIterator;
const defaultParams = {
	json: false,
	limit: 1e3
};
_Symbol$asyncIterator = Symbol.asyncIterator;
var TableCursor = class {
	constructor(args) {
		this.endReached = false;
		this.rowsCount = 0;
		this.maxRows = Number.MAX_SAFE_INTEGER;
		this.abi = ABI.from(args.abi);
		this.client = args.client;
		this.params = {
			...defaultParams,
			...args.params
		};
		if (args.maxRows) this.maxRows = args.maxRows;
		const table = this.abi.tables.find((t) => Name.from(t.name).equals(this.params.table));
		if (!table) throw new Error("Table not found");
		this.type = table.type;
	}
	get nextkey() {
		return this.next_key;
	}
	async *[_Symbol$asyncIterator]() {
		while (true) {
			const rows = await this.next();
			for (const row of rows) yield row;
			if (rows.length === 0 || !this.next_key) return;
		}
	}
	async reset() {
		this.next_key = void 0;
		this.endReached = false;
		this.rowsCount = 0;
	}
	async all() {
		const rows = [];
		for await (const row of this) rows.push(row);
		return rows;
	}
	getTableRowsParams(rowsPerAPIRequest = Number.MAX_SAFE_INTEGER) {
		let lower_bound = this.params.lower_bound;
		if (this.next_key) lower_bound = this.next_key;
		const rowsRemaining = this.maxRows - this.rowsCount;
		const limit = Math.min(rowsRemaining, rowsPerAPIRequest, this.params.limit === void 0 ? Infinity : UInt32.from(this.params.limit).toNumber());
		return {
			...this.params,
			limit,
			lower_bound: wrapIndexValue(lower_bound),
			upper_bound: wrapIndexValue(this.params.upper_bound)
		};
	}
};
//#endregion
//#region src/contract/row-cursor.ts
var TableRowCursor = class extends TableCursor {
	async next(rowsPerAPIRequest = Number.MAX_SAFE_INTEGER) {
		if (this.endReached) return [];
		const query = this.getTableRowsParams(rowsPerAPIRequest);
		const result = await this.client.v1.chain.get_table_rows(query);
		const rows = this.params.json === false && !query.type ? result.rows.map((data) => Serializer.decode({
			data,
			abi: this.abi,
			type: this.type
		})) : result.rows;
		this.next_key = result.next_key;
		this.rowsCount += rows.length;
		if (!result.next_key || rows.length === 0 || this.rowsCount === this.maxRows) this.endReached = true;
		return rows;
	}
};
//#endregion
//#region src/contract/scope-cursor.ts
var TableScopeCursor = class extends TableCursor {
	async next(rowsPerAPIRequest = Number.MAX_SAFE_INTEGER) {
		if (this.endReached) return [];
		let lower_bound = this.params.lower_bound;
		if (this.next_key) lower_bound = this.next_key;
		const rowsRemaining = this.maxRows - this.rowsCount;
		const limit = Math.min(rowsRemaining, rowsPerAPIRequest, this.params.limit === void 0 ? Infinity : UInt32.from(this.params.limit).toNumber());
		const query = {
			code: this.params.code,
			table: this.params.table,
			limit,
			lower_bound: lower_bound ? String(lower_bound) : void 0,
			upper_bound: this.params.upper_bound ? String(this.params.upper_bound) : void 0
		};
		const result = await this.client.v1.chain.get_table_by_scope(query);
		const rows = result.rows;
		this.next_key = result.more;
		this.rowsCount += rows.length;
		if (!result.more || rows.length === 0 || this.rowsCount === this.maxRows) this.endReached = true;
		return rows;
	}
};
//#endregion
//#region src/contract/table.ts
var Table = class Table {
	constructor(args) {
		this.debug = false;
		this.defaultRowLimit = 1e3;
		this.abi = ABI.from(args.abi);
		this.account = Name.from(args.account);
		this.name = Name.from(args.name);
		this.client = args.client;
		this.rowType = args.rowType;
		this.fieldToIndex = args.fieldToIndex;
		const tableABI = this.abi.tables.find((table) => this.name.equals(table.name));
		if (!tableABI) throw new Error(`Table ${this.name} not found in ABI`);
		this.tableABI = tableABI;
		this.defaultScope = args.defaultScope;
		if (args.debug) this.debug = true;
	}
	static from(tableParams) {
		return new Table(tableParams);
	}
	query(params = {}) {
		const tableRowsParams = {
			table: this.name,
			code: this.account,
			scope: this.resolveScope(params.scope),
			type: this.rowType,
			json: this.debug,
			index_position: params.index_position,
			key_type: params.key_type,
			lower_bound: wrapIndexValue(params.from),
			upper_bound: wrapIndexValue(params.to),
			limit: params.rowsPerAPIRequest || this.defaultRowLimit,
			reverse: params.reverse
		};
		if (params.index) {
			const fieldToIndexMapping = this.getFieldToIndex();
			if (!fieldToIndexMapping[params.index]) throw new Error(`Field ${params.index} is not listed in the ABI under key_names/key_types. Try using 'index_position' instead.`);
			tableRowsParams.index_position = fieldToIndexMapping[params.index].index_position;
		}
		return new TableRowCursor({
			abi: this.abi,
			client: this.client,
			maxRows: params.maxRows,
			params: tableRowsParams
		});
	}
	async get(value, params = {}) {
		const tableRowsParams = {
			table: this.name,
			code: this.account,
			scope: this.resolveScope(params.scope),
			type: this.rowType,
			limit: 1,
			lower_bound: wrapIndexValue(value),
			upper_bound: wrapIndexValue(value),
			index_position: params.index_position,
			key_type: params.key_type,
			json: this.debug,
			reverse: params.reverse
		};
		if (params.index) {
			const fieldToIndexMapping = this.getFieldToIndex();
			if (!fieldToIndexMapping[params.index]) throw new Error(`Field ${params.index} is not listed in the ABI under key_names/key_types. Try using 'index_position' instead.`);
			tableRowsParams.index_position = fieldToIndexMapping[params.index].index_position;
		}
		const { rows } = await this.client.v1.chain.get_table_rows(tableRowsParams);
		if (rows.length === 0) return;
		let [row] = rows;
		if (this.debug) return row;
		if (!this.rowType) row = Serializer.decode({
			data: row,
			abi: this.abi,
			type: this.tableABI.type
		});
		if (params.json) row = Serializer.objectify(row);
		return row;
	}
	first(maxRows, params = {}) {
		return this.query({
			...params,
			maxRows
		});
	}
	async all(params = {}) {
		return this.query(params).all();
	}
	resolveScope(scope) {
		const value = isAbsentScope(scope) ? this.defaultScope : scope;
		if (isAbsentScope(value)) return this.account;
		return wrapScopeValue(value);
	}
	getFieldToIndex() {
		if (this.fieldToIndex) return this.fieldToIndex;
		const fieldToIndex = {};
		for (let i = 0; i < this.tableABI.key_names.length; i++) fieldToIndex[this.tableABI.key_names[i]] = {
			type: this.tableABI.key_types[i],
			index_position: indexPositionInWords(i)
		};
		return fieldToIndex;
	}
	scopes(params = {}) {
		const tableRowsParams = {
			code: this.account,
			table: this.name,
			lower_bound: wrapIndexValue(params.from),
			upper_bound: wrapIndexValue(params.to),
			limit: params.rowsPerAPIRequest || this.defaultRowLimit,
			reverse: params.reverse
		};
		return new TableScopeCursor({
			abi: this.abi,
			client: this.client,
			maxRows: params.maxRows,
			params: tableRowsParams
		});
	}
};
//#endregion
//#region src/contract.ts
var Contract = class {
	constructor(args, options = {}) {
		this.debug = false;
		if (!args.abi) throw new Error("Contract requires an ABI");
		this.abi = ABI.from(args.abi);
		if (!args.account) throw new Error("Contract requires an account name");
		this.account = Name.from(args.account);
		if (!args.client) throw new Error("Contract requires an APIClient");
		this.client = args.client;
		if (options.debug) this.debug = options.debug;
	}
	get tableNames() {
		return this.abi.tables.map((table) => String(table.name));
	}
	hasTable(name) {
		return this.tableNames.includes(String(name));
	}
	table(name, scope, rowType) {
		if (!this.hasTable(name)) throw new Error(`Contract (${this.account}) does not have a table named (${name})`);
		return Table.from({
			abi: this.abi,
			account: this.account,
			client: this.client,
			debug: this.debug,
			defaultScope: scope,
			name,
			rowType
		});
	}
	get actionNames() {
		return this.abi.actions.map((action) => String(action.name));
	}
	hasAction(name) {
		return this.actionNames.includes(String(name));
	}
	action(name, data, options) {
		if (!this.hasAction(name)) throw new Error(`Contract (${this.account}) does not have an action named (${name})`);
		let authorization = [PlaceholderAuth];
		if (options && options.authorization) authorization = options.authorization.map((auth) => PermissionLevel.from(auth));
		return Action.from({
			account: this.account,
			name,
			authorization,
			data
		}, this.abi);
	}
	async readonly(name, data) {
		if (!data) data = {};
		const action = this.action(name, data);
		action.authorization = [];
		const transaction = Transaction.from({
			ref_block_num: 0,
			ref_block_prefix: 0,
			expiration: 0,
			actions: [action]
		});
		const response = await this.client.v1.chain.send_read_only_transaction(transaction);
		if (response.processed.except) throw new Error(formatExceptionMessage(response.processed.except));
		const hexData = response.processed.action_traces[0].return_value_hex_data;
		const returnType = this.abi.action_results.find((a) => Name.from(a.name).equals(name));
		if (!returnType) throw new Error(`Return type for ${name} not defined in the ABI.`);
		return Serializer.decode({
			data: hexData,
			type: returnType.result_type,
			abi: this.abi
		});
	}
	actions(actions, options) {
		return actions.map((action) => this.action(action.name, action.data, { authorization: action.authorization || options?.authorization }));
	}
	ricardian(name) {
		if (!this.hasAction(name)) throw new Error(`Contract (${this.account}) does not have an action named (${name})`);
		const action = this.abi.actions.find((action) => Name.from(action.name).equals(name));
		if (!action || !action.ricardian_contract) throw new Error(`Contract (${this.account}) action named (${name}) does not have a defined ricardian contract`);
		return action.ricardian_contract;
	}
};
//#endregion
//#region src/kit.ts
const defaultContractKitOptions = {};
var ContractKit = class {
	constructor(args, options = defaultContractKitOptions) {
		this.debug = false;
		if (args.client) this.client = args.client;
		else throw new Error("A `client` must be passed when initializing the ContractKit.");
		if (options.abiCache) this.abiCache = options.abiCache;
		else this.abiCache = new ABICache(this.client);
		if (options.abis) options.abis.forEach(({ name, abi }) => this.abiCache.setAbi(Name.from(name), ABI.from(abi)));
		if (options.debug) this.debug = options.debug;
	}
	async load(contract) {
		const account = Name.from(contract);
		const abiDef = await this.abiCache.getAbi(account);
		return new Contract({
			abi: ABI.from(abiDef),
			account,
			client: this.client
		}, { debug: this.debug });
	}
};
//#endregion
//#region src/index.ts
var src_default = ContractKit;
//#endregion
export { Contract, ContractKit, Table, TableRowCursor, TableScopeCursor, abiToBlob, blobStringToAbi, capitalize, src_default as default, formatExceptionMessage, indexPositionInWords, isAbsentScope, pascalCase, singularize, wrapIndexValue, wrapScopeValue };

//# sourceMappingURL=contract.m.js.map